import { useEffect, useRef, useState } from 'react';

/**
 * Telling somebody which field is wrong, and taking them to it.
 *
 * Extracted the moment a second form needed it. The behaviour is the
 * part worth sharing, not the rules: each page passes its own `checks`,
 * keyed by the input's id, because what makes an age valid has nothing
 * to do with what makes a password valid. Keying on the id is what lets
 * this find the element to jump to without a ref per field.
 *
 * Two decisions live here rather than in either page, so both pages
 * cannot answer them differently:
 *
 *   - nothing is marked until the form is submitted. Reddening a field
 *     a child has not finished typing is telling them off for being
 *     slow;
 *   - once marked, a field stays marked until it is genuinely right,
 *     not until the next keystroke. Everything already marked is
 *     re-checked on every change, which is also how fixing a password
 *     settles the confirm box under it.
 *
 * `checks[id](values)` returns null when the field is fine, or the
 * sentence to show under it. `order` is the fields top to bottom, so
 * "the first thing wrong" means the first one they would have reached.
 *
 * A problem may also be `true` rather than a sentence: the field is
 * marked and flagged, but says nothing under itself. Sign-in needs
 * that - it has to redden both boxes without writing the same sentence
 * beneath each of them.
 */
export function useFieldCheck(checks, order) {
  const [problems, setProblems] = useState({});
  const jumpTo = useRef(null);

  /*
   * Moving happens after the render, not in the submit handler: the
   * messages appearing under the fields change where everything is, so
   * a position measured before that lands somewhere else. focus() is
   * given preventScroll so the browser's own jump does not fight the
   * centred one.
   */
  useEffect(() => {
    const id = jumpTo.current;
    if (!id) return;
    jumpTo.current = null;
    const el = document.getElementById(id);
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  const goToFirst = (bad) => {
    const first = order.find((id) => bad[id]);
    if (first) jumpTo.current = first;
  };

  /** Everything, on submit. Returns the problems - empty when it is fine. */
  const checkAll = (values) => {
    const bad = {};
    for (const id of order) {
      const problem = checks[id](values);
      if (problem) bad[id] = problem;
    }
    setProblems(bad);
    goToFirst(bad);
    return bad;
  };

  /** Re-checks what is already marked. Call from every onChange. */
  const recheck = (values) => {
    setProblems((bad) => {
      if (!Object.keys(bad).length) return bad;
      const still = {};
      for (const id of Object.keys(bad)) {
        const problem = checks[id](values);
        if (problem) still[id] = problem;
      }
      return still;
    });
  };

  /**
   * Marks fields the server complained about, and goes to the first -
   * or to `focusId`, when the useful box is not the top one. Sign-in
   * marks both of its fields but sends the cursor to the password,
   * because that is the one it just emptied.
   */
  const mark = (bad, focusId) => {
    setProblems(bad);
    if (focusId && bad[focusId]) jumpTo.current = focusId;
    else goToFirst(bad);
  };

  const clear = () => setProblems({});

  /** The wrapper's class, so the border and background turn red. */
  const cls = (id, base = 'field') => `${base} ${problems[id] ? 'is-bad' : ''}`;

  /**
   * Marks the input itself. Colour is never the only signal - plenty of
   * children cannot tell the red border from the ordinary one.
   */
  const flag = (id, describedBy) => ({
    'aria-invalid': problems[id] ? 'true' : undefined,
    'aria-describedby':
      [problems[id] ? `${id}-error` : null, describedBy].filter(Boolean).join(' ') || undefined,
  });

  /** The message under the field, tied to the input by id. */
  const error = (id) =>
    typeof problems[id] === 'string' && problems[id] ? (
      <p className="field-error" id={`${id}-error`}>
        <span aria-hidden="true">⚠️</span>
        {problems[id]}
      </p>
    ) : null;

  return { problems, checkAll, recheck, mark, clear, cls, flag, error };
}

/** The line above the form, counting what is waiting below it. */
export const countProblems = (problems) => {
  const n = Object.keys(problems).length;
  return n === 1
    ? 'One thing needs fixing — it is marked in red below.'
    : `${n} things need fixing — they are marked in red below.`;
};
